/**
 * Pure rules for the live canvas's zoom/pan VIEW STATE. The projection maths
 * itself lives in FloorPlanModal/utils/viewport.js (`createViewport`,
 * `zoomAtPoint`); this file only decides what a LEGAL view is -- how far you
 * may zoom, and when a mouse gesture counts as a drag rather than a click.
 * The pan is never clamped (D-29): off-site people and machines are drawn
 * around the site, so the operator may pan off it, map or not.
 *
 * The view is `{ zoom, pan }` and feeds straight into `createViewport`, i.e.
 *   pixel = meters * scale * zoom + pan
 * so `zoom: 1, pan: {x:0,y:0}` is exactly the fit-to-floor rendering the
 * canvas had before zoom existed. That identity is the regression bar: every
 * helper here must return the identity view unchanged.
 */

// 1 = fit the whole floor: the identity view, where ⟲ returns to.
// 8 = ~5 px per metre on a 400m site in a ~1000px canvas, enough to separate
// two workers standing a metre apart.
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 8;
// How far a plane canvas zooms OUT, with or without the map backdrop (D-29):
// 0.02x shows ~50x the floor, so the area around the site and the off-site
// people and machines drawn there come into view.
export const MIN_ZOOM_GEO = 0.02;

// Multiplicative per wheel notch: ~13 notches from fit to 8x, and zooming
// out retraces exactly the same zoom levels as zooming in.
export const ZOOM_FACTOR_PER_NOTCH = 1.2;

// Mouse travel (px) before a press-move-release stops being a click and
// becomes a pan. Below it the click must still reach its target: the canvas
// clears the personnel selection, stations and machines open popovers.
export const DRAG_THRESHOLD_PX = 4;

export const IDENTITY_VIEW = Object.freeze({
  zoom: MIN_ZOOM,
  pan: Object.freeze({ x: 0, y: 0 }),
});

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const clampZoom = (zoom) => clamp(zoom, MIN_ZOOM_GEO, MAX_ZOOM);

/**
 * Next zoom level for one wheel event. `deltaY < 0` (wheel up / pinch out on
 * a trackpad) zooms IN, matching every map UI. The view passes through fit
 * (MIN_ZOOM) on its way below it, and snaps to exactly 1 there when float
 * error would otherwise leave 1.0000000000000002 behind -- `isIdentity` and
 * the "user has zoomed" signal both compare against 1 exactly.
 */
export const nextZoom = (zoom, deltaY) => {
  if (!deltaY) return zoom;
  const factor = deltaY < 0 ? ZOOM_FACTOR_PER_NOTCH : 1 / ZOOM_FACTOR_PER_NOTCH;
  const next = clampZoom(zoom * factor);
  return Math.abs(next - MIN_ZOOM) < 1e-6 ? MIN_ZOOM : next;
};

export const exceedsDragThreshold = (dx, dy) =>
  Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX;

export const isIdentityView = (view) =>
  view?.zoom === MIN_ZOOM && view?.pan?.x === 0 && view?.pan?.y === 0;
